// Utility helpers to detect prerender/headless environments (react-snap / puppeteer)
// and to provide safe wrappers for fetch/3rd-party initialization during prerendering.

// isPrerender(): returns true when running inside a headless renderer (Puppeteer/HeadlessChrome)
export function isPrerender() {
  try {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // Headless Chrome (Puppeteer) includes HeadlessChrome in the UA. React-snap / other renderers
    // may also include identifiable strings; keep the pattern broad but conservative.
    return /HeadlessChrome|PhantomJS|puppeteer|ReactSnap|Prerender/i.test(ua);
  } catch (e) {
    return false;
  }
}

// safeFetchJson: wrapper around fetch that avoids making network calls during prerendering
// and gracefully handles responses that are not valid JSON (e.g., an index.html returned).
export async function safeFetchJson(url, opts = {}) {
  if (isPrerender()) {
    // Return null or a minimal fallback during prerender. Components should handle nulls.
    return null;
  }

  const res = await fetch(url, opts);
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (err) {
    // If the server returned HTML (starts with '<') or any non-JSON, return null and warn.
    // This prevents `Unexpected token < in JSON at position 0` during prerender.
    // Caller should handle null appropriately.
    // eslint-disable-next-line no-console
    console.warn('safeFetchJson: failed to parse JSON for', url, err && err.message);
    return null;
  }
}

// safeLoadThirdParty: helper to skip third-party initializers (e.g., Stripe) during prerender.
// Usage example:
// if (!isPrerender()) { loadStripeScript(); }
export function safeLoadThirdParty(initFn) {
  if (isPrerender()) return null;
  try {
    return initFn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('safeLoadThirdParty failed', e && e.message);
    return null;
  }
}

// During prerendering (react-snap / puppeteer) many network requests may fail or be blocked.
// To avoid JSON parse errors and network noise, we can patch `window.fetch` early to return
// a minimal successful JSON response for same-origin API calls. Call this early from `src/index.js`.
export function patchFetchForPrerender() {
  if (!isPrerender()) return;
  try {
    // eslint-disable-next-line no-undef
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      try {
        // Basic URL normalization
        const url = typeof input === 'string' ? input : input.url || '';
        // Treat same-origin (relative) API calls as returning empty JSON array/object
        const isSameOrigin = url.startsWith('/') || url.startsWith(window.location.origin);
        if (isSameOrigin) {
          // Return a minimal Response-like object with json() and text()
          return {
            ok: true,
            status: 200,
            text: async () => '[]',
            json: async () => []
          };
        }
        // For third party requests, return a rejected promise to mimic blocked requests
        return Promise.reject(new Error('Prerender: blocked third-party request'));
      } catch (e) {
        // Fallback to original fetch when available
        try { return originalFetch(input, init); } catch (e2) { return Promise.reject(e); }
      }
    };
  } catch (e) {
    // ignore
  }
}
